<template>
  <div>
    <div v-if="loading && !initialized">
      <div
        class="relative mx-auto my-4 text-center"
        style="max-width: 300px">
        <p class="font-sm mb-1 font-light uppercase tracking-widest opacity-70">
          {{ $t("message.loadingResults") }}
        </p>
        <progress-bar />
      </div>
    </div>
    <template v-else-if="initialized">
      <rich-embed
        :creator="creator"
        :loading="loading"
        :params="params"
        :results="results"
        :selected-result="selectedResult"
        :geo-type="geoType" />

      <template v-if="showParams">
        <h2 class="mt-8">
          {{ params }}
        </h2>

        <a
          class="my-4 block text-blue-600"
          :href="`https://e.ddhq.io/client/3.0/${queryParams}`"
          target="_blank">
          Prod Version
        </a>
      </template>
    </template>
    <error-dialogue
      v-else-if="debug && error"
      :error="error" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { apiUrl } from "@/config";
import { getGeoType } from "@/helpers/utils";
import DataMixin from "@/mixins/data";
import ProgressBar from "@/components/ui/ProgressBar";
import RichEmbed from "./RichEmbed";
import ErrorDialogue from "./ErrorDialogue";
import { formatDate } from "../helpers/formatting";

const components = {
  ProgressBar,
  RichEmbed,
  ErrorDialogue,
};

const props = {
  debug: {
    type: Boolean,
    required: false,
    default: false,
  },
  params: {
    type: Object,
    required: true,
  },
  creator: {
    type: Boolean,
    required: false,
  },
};

const mixins = [DataMixin];

const stateHouseNames = ["State House", "State Assembly", "House of Delegates", "General Assembly"];
const nonCountyOffices = [...stateHouseNames, "US House", "State Senate"];
const vcuStates = [
  "Maine",
  "Massachusetts",
  "New Hampshire",
  "Vermont",
  "Connecticut",
  "Rhode Island",
];

const computed = {
  ...mapGetters([
    "cds",
    "colors",
    "counties",
    "cities",
    "missingFips",
    "error",
    "sldls",
    "sldus",
    "states",
    "theme",
    "vcus",
    "selectedResultIndex",
  ]),
  haveCountyOfficeRaces() {
    return this.results.find(
      (race) =>
        nonCountyOffices.includes(race.office) || (true && !vcuStates.includes(race.state_name))
    );
  },
  selectedResult() {
    return this.results[this.selectedResultIndex];
  },
  showStates() {
    return (
      this.params.mapType === "Nationwide" &&
      !this.results.find((race) => race.office === "US House")
    );
  },
  showCounties() {
    return this.haveCountyOfficeRaces;
  },
  showCongressionalDistricts() {
    const haveUSHouseRaces = this.results.find((race) => race.office === "US House") || false;
    return haveUSHouseRaces;
  },
  showVCUs() {
    return this.vcuStates.size > 0;
  },
  showStateSenate() {
    const haveStateSenateRaces =
      this.results.find((race) => race.office === "State Senate") || false;
    return haveStateSenateRaces;
  },
  showStateHouse() {
    const haveStateHouseRaces =
      this.results.find((race) => stateHouseNames.includes(race.office)) || false;
    return haveStateHouseRaces;
  },
  stateSenateStates() {
    const stateSenateRaces = this.results.filter((race) => race.office === "State Senate") || [];
    const states = new Set(stateSenateRaces.map((race) => race.state_name));

    return states;
  },
  stateHouseStates() {
    const stateHouseRaces =
      this.results.filter((race) => stateHouseNames.includes(race.office)) || [];
    const states = new Set(stateHouseRaces.map((race) => race.state_name));

    return states;
  },
  vcuStates() {
    const vcuRaces =
      this.results.filter(
        (race) => vcuStates.includes(race.state_name) && !nonCountyOffices.includes(race.office)
      ) || [];
    const states = new Set(vcuRaces.map((race) => race.state_name));

    return states;
  },
};

const methods = {
  ...mapActions([
    "configureApi",
    "fetchCds",
    "fetchCounties",
    "fetchCities",
    "fetchSldus",
    "fetchSldls",
    "fetchStates",
    "fetchTopojson",
    "fetchVcus",
    "fetchVcuResults",
    "fetchFips",
    "setGeoSelection",
    "setPartyColors",
    "setSelectedResultIndex",
    "toggleShowMoreCandidates",
    "setTheme",
  ]),
  ...mapActions({
    fetchCongressControl: "congressControl/fetchCongressControl",
  }),
  async loadData() {
    if (this.params.mapType !== "Nationwide Control Tracker") await this.fetchData();
    if (this.params.congressControlDataURL && this.params.nationwideControlOffice !== "President") {
      await this.fetchCongressControl(this.params.congressControlDataURL);
    }

    const pollingInterval = 60 * 1000;
    this.lastUpdated = formatDate(new Date());
    this.timeout = setTimeout(this.loadData, pollingInterval);
  },
  loadCss() {
    const existingTheme = document.getElementById("theme");

    if (existingTheme) {
      existingTheme.remove();
    }

    if (process.env.NODE_ENV === "development") {
      document.body.style.backgroundColor = this.theme === "ddhq-dark" ? "#000" : "#FFF";
    }

    let file = document.createElement("link");
    file.rel = "stylesheet";
    file.href = `themes/${this.theme}.css`;
    file.id = "theme";

    let ddhqFile = document.createElement("link");
    ddhqFile.rel = "stylesheet";
    ddhqFile.href = `themes/main.css`;

    document.head.appendChild(ddhqFile);
    document.head.appendChild(file);
  },
  setLocale() {
    if (this.params.spanish && this.$i18n.locale !== "es") {
      this.$i18n.locale = "es";
    } else {
      this.$i18n.locale = "en";
    }
  },
  setInitialRace() {
    if (this.params.tabs && this.params.tabs.length > 0) {
      const defaultRace = this.results.filter((r) => r.office === this.params.tabs[0])[0];
      this.setSelectedResultIndex(this.results.indexOf(defaultRace));
    }
  },
  setSelectedResult() {
    if (this.params.mapType === "Nationwide") {
      this.setSelectedResultIndex(null);
    } else {
      this.setSelectedResultIndex(0);
    }
  },
  finishLoading() {
    this.setSelectedResult();
    this.loading = false;
    this.initialized = true;

    this.setInitialRace();
  },
  onParentMessage(event) {
    if (event.source === document.parentElement) return;
    if (event.data?.name !== "toggle-theme" || event.data.dark === undefined) return;

    const theme = event.data.dark === true ? "ddhq-dark" : "ddhq";

    this.setTheme(theme);
  },
};

const watch = {
  selectedResultIndex() {
    this.geoType = getGeoType(this.selectedResult?.state_name);
  },
  theme() {
    this.loadCss();
  },
};

export default {
  components,
  mixins,
  props,
  data: () => ({
    lastUpdated: null,
    vcusLoading: false,
    initialized: false,
    showError: false,
    timeout: null,
    showParams: process.env.NODE_ENV === "development",
    queryParams: window.location.search,
  }),
  computed,
  watch,
  beforeMount() {
    this.setLocale();
    this.loading = true;
    this.configureApi({ apiUrl: apiUrl });
    if (this.error) {
      this.clearError();
    }

    this.loadCss();

    this.loadData().then(() => {
      const promises = [];

      const showMap = this.params.mapType !== "" || this.params.showMiniMap;

      if (showMap) {
        if (this.params.topojsonUrl) {
          promises.push(this.fetchTopojson(this.params.topojsonUrl));
        } else {
          if (Object.keys(this.sldus).length === 0 && this.showStateSenate) {
            promises.push(this.fetchSldus(this.stateSenateStates));
          }
          if (Object.keys(this.sldls).length === 0 && this.showStateHouse) {
            promises.push(this.fetchSldls(this.stateHouseStates));
          }
          if (this.states.length === 0 && this.showStates) {
            promises.push(this.fetchStates());
          }
          if (this.counties.length === 0 && this.showCounties) {
            promises.push(this.fetchCounties());
          }
          if (this.cds.length === 0 && this.showCongressionalDistricts) {
            promises.push(this.fetchCds());
          }
          if (Object.keys(this.sldus).length === 0 && this.showVCUs) {
            promises.push(this.fetchVcus(this.vcuStates));
          }
        }

        if (this.params.showCities && this.cities.length === 0) {
          promises.push(this.fetchCities());
        }
      }

      if (this.missingFips) {
        promises.push(this.fetchFips());
      }

      Promise.all(promises).finally(() => {
        this.finishLoading();
      });
    });

    window.addEventListener("message", this.onParentMessage, false);
  },
  beforeUnmount() {
    clearTimeout(this.timeout);

    window.removeEventListener("message", this.onParentMessage, false);
  },
  methods,
};
</script>
